import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type { CurrentMember } from './types/member';
import type {
  ApiMessageResponse,
  CurrentUser,
  CreateTenantPayload,
  CreateTenantResult,
  DeleteTenantResult,
  ExchangeCodeForTokenPayload,
  JoinTenantPayload,
  JoinTenantResult,
  LogoutResult,
  OAuth2TokenResult,
  RegisterParams,
  RegisterResult,
  ResetPasswordPayload,
  SendSmsCodePayload,
  SwitchTenantResult,
  TenantByCodeResult,
  TenantContext,
} from './types/auth';
import { BizAppConfig } from 'config/application.manifest';

const request = createAppRequest();

export async function getCurrentMemberUser(options?: ApiRequestOptions) {
  const member = await request<CurrentMember>('/bff/iam-ui/members/me', {
    method: 'GET',
    ...(options || {}),
  });

  return {
    id: member.userUuid,
    uuid: member.userUuid,
    username: member.username,
    nickname: member.nickname || member.name || member.username,
    name: member.name || member.nickname || member.username,
    phone: member.phone,
    email: member.email,
    avatar: member.avatarUrl || member.photo,
    tenantUuid: member.tenantUuid,
    organizationUuid: member.organizationUuid,
    memberUuid: member.memberUuid,
    isAdmin: member.isAdmin === true,
    lastLoginOrganizationUuid: member.organizationUuid,
  } satisfies CurrentUser;
}

export async function refreshToken(
  refreshTokenValue: string,
  ssoId?: string,
  options?: ApiRequestOptions,
) {
  return request<OAuth2TokenResult>('/bff/auth/refreshToken', {
    method: 'POST',
    params: {
      refreshToken: refreshTokenValue,
      sso_id: ssoId as string,
    },
    ...(options || {}),
  });
}

export async function exchangeCodeForToken(
  body: ExchangeCodeForTokenPayload,
  options?: ApiRequestOptions,
) {
  return request<OAuth2TokenResult>('/bff/auth/codeLogin', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function register(body: RegisterParams, options?: ApiRequestOptions) {
  return request<RegisterResult>('/bff/auth/register', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function sendSmsCode(
  body: SendSmsCodePayload,
  options?: ApiRequestOptions,
) {
  return request<ApiMessageResponse>('/bff/auth/sendSmsCode', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function resetPassword(
  body: ResetPasswordPayload,
  options?: ApiRequestOptions,
) {
  return request<ApiMessageResponse>('/bff/auth/resetPassword', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function getTenantList(options?: ApiRequestOptions) {
  return request<TenantContext[]>('/bff/auth/tenant-list', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getTenantByCode(
  code: string,
  options?: ApiRequestOptions,
) {
  return request<TenantByCodeResult>('/bff/auth/tenant-by-code', {
    method: 'GET',
    params: { code },
    ...(options || {}),
  });
}

export async function switchTenant(
  organizationUuid: string,
  options?: ApiRequestOptions,
) {
  return request<SwitchTenantResult>('/bff/auth/switch-tenant', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: { organizationUuid },
    ...(options || {}),
  });
}

export async function joinTenant(
  body: JoinTenantPayload,
  options?: ApiRequestOptions,
) {
  return request<JoinTenantResult>('/bff/auth/join-tenant', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function createTenant(
  body: CreateTenantPayload,
  options?: ApiRequestOptions,
) {
  return request<CreateTenantResult>('/bff/auth/tenant', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function deleteTenant(
  tenantUuid: string,
  options?: ApiRequestOptions,
) {
  return request<DeleteTenantResult>(`/bff/auth/tenant/${tenantUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function logout(ssoId?: string, options?: ApiRequestOptions) {
  return request<LogoutResult>('/bff/auth/logout', {
    method: 'POST',
    params: {
      sso_id: ssoId,
    },
    ...(options || {}),
  });
}
